import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Lock, Eye, EyeSlash } from '@phosphor-icons/react'

import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/primitives'
import { PinDayLogo } from '@/components/ui/PinDayLogo'

export function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const params = useParams<{ token: string }>()

  const token = params.token ?? ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const canSubmit = useMemo(() => {
    if (!token) return false
    if (password.length < 6) return false
    if (password !== confirmPassword) return false
    return true
  }, [confirmPassword, password, token])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!token) {
        toast.error(t('errors.required'))
        return
      }

      if (password.length < 6) {
        toast.error(t('errors.weakPassword'))
        return
      }

      if (password !== confirmPassword) {
        toast.error(t('register.passwordMismatch'))
        return
      }

      setIsLoading(true)
      try {
        await api.resetPassword(token, password)
        toast.success(t('resetPassword.resetSuccess'))
        navigate('/login')
      } catch (error) {
        toast.error(t('errors.invalidCredentials'))
      } finally {
        setIsLoading(false)
      }
    },
    [confirmPassword, navigate, password, t, token],
  )

  return (
    <div className="min-h-screen bg-[var(--pd-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center">
            <PinDayLogo size="xl" />
          </Link>
          <p className="text-[var(--pd-foreground)]/60 mt-2">{t('resetPassword.subtitle')}</p>
        </div>

        <Card>
          <div className="p-8">
            <h1 className="text-xl font-semibold text-[var(--pd-foreground)]">{t('resetPassword.title')}</h1>

            <form onSubmit={handleSubmit} className="space-y-6 mt-6">
              <div className="space-y-2">
                <Label htmlFor="password">{t('resetPassword.newPassword')}</Label>
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40 hover:text-[var(--pd-foreground)] transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('resetPassword.confirmNewPassword')}</Label>
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('resetPassword.confirmNewPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !canSubmit}
                className="w-full bg-[var(--pd-primary)] hover:bg-[var(--pd-primary)]/90 text-white py-6 rounded-xl font-semibold text-base shadow-lg transition-all duration-200"
              >
                {isLoading ? t('resetPassword.submitting') : t('resetPassword.resetSubmit')}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-[var(--pd-primary)] hover:text-[var(--pd-primary)]/80 font-semibold transition-colors"
                >
                  {t('resetPassword.backToLogin')}
                </Link>
              </div>
            </form>
          </div>
        </Card>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-[var(--pd-foreground)]/60 hover:text-[var(--pd-foreground)] transition-colors"
          >
            ← {t('common:actions.back')}
          </Link>
        </div>
      </div>
    </div>
  )
}
